/**
 * Reading a Data frame's server-written summary (its content and card share
 * one shape) and showing a cell's value as text.
 */
import type {
  DatasetField,
  DatasetFieldType,
  DatasetOption,
  DatasetValue,
} from "@/api/datasets";
import { asRecord } from "@/components/shapes/shared/plain-record";
import { formatDate, formatNumber, translate } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { framesDatasetMessages } from "@/i18n/messages/frames-dataset";

/** A stored UTC time, shown in the person's timezone. */
const DATETIME: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

const FIELD_TYPES: ReadonlySet<string> = new Set<DatasetFieldType>([
  "text",
  "number",
  "decimal",
  "boolean",
  "date",
  "datetime",
  "select",
]);

export interface DatasetSummaryField {
  id: string;
  name: string;
  type: DatasetFieldType;
}

export interface DatasetSummaryRow {
  id: string;
  /** Keyed by field id; a select shows its label. */
  values: Record<string, DatasetValue>;
}

export interface DatasetSummary {
  dataRevision: number;
  schemaRevision: number;
  rowCount: number;
  fields: DatasetSummaryField[];
  rows: DatasetSummaryRow[];
}

function count(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function fieldFrom(value: unknown): DatasetSummaryField[] {
  const field = asRecord(value);
  return field && typeof field.id === "string"
    ? [
        {
          id: field.id,
          name: typeof field.name === "string" ? field.name : "",
          type: FIELD_TYPES.has(String(field.type))
            ? (field.type as DatasetFieldType)
            : "text",
        },
      ]
    : [];
}

function rowFrom(value: unknown): DatasetSummaryRow[] {
  const row = asRecord(value);
  const values = asRecord(row?.values);
  return row && typeof row.id === "string" && values
    ? [{ id: row.id, values: values as Record<string, DatasetValue> }]
    : [];
}

/** The summary in a Data frame's content or card; anything else reads empty. */
export function datasetSummaryFrom(
  source: Record<string, unknown>,
): DatasetSummary {
  return {
    dataRevision: count(source.data_revision),
    schemaRevision: count(source.schema_revision),
    rowCount: count(source.row_count),
    fields: Array.isArray(source.fields)
      ? source.fields.flatMap(fieldFrom)
      : [],
    rows: Array.isArray(source.rows) ? source.rows.flatMap(rowFrom) : [],
  };
}

/** True for the types drawn right-aligned in tabular numbers. */
export function isNumericField(type: DatasetFieldType): boolean {
  return type === "number" || type === "decimal";
}

/**
 * A cell as text: an empty cell is "", a select's option id its label (a
 * card already holds labels), a number and a time in the app language.
 */
export function cellText(
  value: DatasetValue | undefined,
  type: DatasetFieldType,
  options?: readonly DatasetOption[],
): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") {
    return translate(commonMessages, value ? "yes" : "no");
  }
  if (typeof value === "number") {
    return type === "number" ? formatNumber(value) : String(value);
  }
  if (type === "select") {
    return options?.find((option) => option.id === value)?.label ?? value;
  }
  if (type === "datetime" && !Number.isNaN(Date.parse(value))) {
    return formatDate(value, DATETIME);
  }
  return value;
}

/**
 * What an empty cell shows, faintly, so it reads as a place to write: its
 * field's description when the table keeps one, else "Add {field}".
 */
export function cellPlaceholder(
  field: Pick<DatasetField, "name" | "description">,
): string {
  return (
    field.description?.trim() ||
    translate(framesDatasetMessages, "addValue", { field: field.name })
  );
}
